import React, { useState, useEffect } from 'react';
import { Shield, ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { API } from '../config';

const AdminLogin = ({ onBack, DashboardComponent }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = sessionStorage.getItem('admin_token');
    if (token) {
      axios.get(`${API}/admin/verify`, { params: { token } })
        .then(() => setIsAuthenticated(true))
        .catch(() => sessionStorage.removeItem('admin_token'))
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem('admin_token', res.data.token);
      setIsAuthenticated(true);
    } catch (err) {
      setError('Incorrect password');
    }
    setIsLoading(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setPassword('');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-sage-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <DashboardComponent onBack={onBack} onLogout={handleLogout} />;
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4" data-testid="admin-login">
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-sage-100 rounded-full mb-4">
            <Shield className="text-sage-600" size={28} />
          </div>
          <h1 className="text-2xl font-semibold text-stone-800">Admin Access</h1>
          <p className="text-stone-500 text-sm mt-1">Dublin Holistic Massage</p>
        </div>

        <form onSubmit={handleLogin} className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200 space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-600 mb-1.5">Password</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="pl-10 pr-10"
                required
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-sm text-center" data-testid="admin-login-error">{error}</p>
          )}

          <Button 
            type="submit" 
            className="w-full bg-sage-600 hover:bg-sage-700 text-white py-5 rounded-xl"
            disabled={isLoading}
            data-testid="admin-login-btn"
          >
            {isLoading ? 'Verifying...' : 'Log In'}
          </Button>
        </form>

        <div className="text-center mt-4">
          <Button variant="ghost" onClick={onBack} className="text-stone-500 text-sm">
            <ArrowLeft size={16} className="mr-1" />
            Back to Website
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
